import { useState } from 'react';
import { useOrders } from '../../hooks/useData';
import { DataTable } from '../../components/shared/KPIComponents';
import { api, getCurrentMonth, formatCurrency, SUPPLIERS } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Trash2, Plus, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

const ChefManuel = () => {
  const currentMonth = getCurrentMonth();
  const { orders, loading, refetch } = useOrders(currentMonth);
  
  const [newOrder, setNewOrder] = useState({ fournisseur: '', montant: '' });
  
  const handleAddOrder = async () => {
    if (!newOrder.fournisseur || !newOrder.montant) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    try {
      await api.post('/orders', {
        fournisseur: newOrder.fournisseur,
        montant: parseFloat(newOrder.montant)
      });
      toast.success('Commande ajoutée');
      setNewOrder({ fournisseur: '', montant: '' });
      refetch();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };
  
  const handleQuickAdd = (supplier) => {
    setNewOrder({ ...newOrder, fournisseur: supplier });
  };
  
  const handleDelete = async (id) => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success('Commande supprimée');
      refetch();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const totalOrders = orders.reduce((sum, o) => sum + o.montant, 0);
  
  const columns = [
    { header: 'Date', render: (row) => <span className="font-mono text-sm">{row.date}</span> },
    { header: 'Fournisseur', render: (row) => <span className="font-medium">{row.fournisseur}</span> },
    { header: 'Montant', render: (row) => <span className="font-mono text-lemas-green">{formatCurrency(row.montant)}</span> },
    { header: '', render: (row) => (
      <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-lemas-danger">
        <Trash2 size={14} />
      </Button>
    )},
  ];
  
  return (
    <div className="space-y-6 animate-slide-up" data-testid="chef-manuel">
      <h2 className="font-title text-2xl flex items-center gap-2">
        <ClipboardList className="text-lemas-green" />
        Commandes manuelles
      </h2>
      
      <p className="text-sm text-[rgb(var(--lm-text-secondary))]">
        Pour les achats sans facture PDF (espèces, marché...)
      </p>
      
      {/* Quick supplier buttons */}
      <div className="flex flex-wrap gap-2">
        {SUPPLIERS.map(supplier => (
          <Button
            key={supplier}
            variant="outline"
            size="sm"
            onClick={() => handleQuickAdd(supplier)}
            className={`border-white/20 hover:border-lemas-green hover:text-lemas-green ${
              newOrder.fournisseur === supplier ? 'border-lemas-green text-lemas-green bg-lemas-green/10' : ''
            }`}
          >
            {supplier}
          </Button>
        ))}
      </div>
      
      {/* Add form */}
      <div className="kpi-card">
        <div className="flex flex-col sm:flex-row gap-3">
          <Input 
            placeholder="Fournisseur"
            value={newOrder.fournisseur}
            onChange={(e) => setNewOrder({...newOrder, fournisseur: e.target.value})}
            className="flex-1 bg-lemas-bg border-[rgb(var(--lm-border))]"
            data-testid="order-supplier-input"
          />
          <Input 
            type="number"
            placeholder="Montant €"
            value={newOrder.montant}
            onChange={(e) => setNewOrder({...newOrder, montant: e.target.value})}
            className="w-32 bg-lemas-bg border-[rgb(var(--lm-border))] font-mono"
            data-testid="order-amount-input"
          />
          <Button 
            onClick={handleAddOrder}
            className="bg-lemas-green text-lemas-bg hover:bg-lemas-green/90"
            data-testid="add-order-btn"
          >
            <Plus size={18} className="mr-2" />
            Ajouter
          </Button>
        </div>
      </div>
      
      {/* Total */}
      <div className="kpi-card border-lemas-green/30 text-center">
        <p className="kpi-label">Total commandes manuelles</p>
        <p className="font-mono text-3xl text-lemas-green">{formatCurrency(totalOrders)}</p>
        <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">{orders.length} commandes ce mois</p>
      </div>
      
      {/* Orders list */}
      <div className="kpi-card">
        <h3 className="font-title text-lg mb-4">Commandes du mois</h3>
        <DataTable 
          columns={columns}
          data={orders}
          emptyMessage="Aucune commande manuelle"
        />
      </div>
    </div>
  );
};

export default ChefManuel;
